import Css from "./style.module.scss";

import coloredLogo from "assets/coloredLogo.svg";
import extensionLogo from "assets/extensionIcon.svg";

import * as Icons from "@phosphor-icons/react";
import { NavLink, useRouteMatch } from "react-router-dom";
import { checkIsBusinessUser } from "selectors/user";
import {
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getGlobalStats,
  getSelectedBusinessData,
  getSelectedBusinessId,
  getUserBusinessesData
} from "selectors/businesses";
import { checkTasksReadyToDisplay } from "selectors/tasks";
import { getActiveOrganization, getProjectName } from "selectors/organizations";
import { getContactsData } from "selectors/contacts";
import { getTextsData } from "selectors/texts";
import { getUploadingArchiveFilesCount } from "selectors/archive";
import { getUploadingDocumentsCount } from "selectors/documents";
import { useSelector } from "react-redux";
import Badge from "nlib/ui/Badge";
import BrowserExtensionModal from "nlib/common/BrowserExtensionModal";
import Countries from "const/Countries";
import IntegrationServices from "const/IntegrationServices";
import LocalStorageKeys from "const/LocalStorageKeys";
import Objects from "utils/Objects";
import Pages from "nlib/pages/Pages";
import Platforms from "utils/Platforms";
import React, { useCallback, useMemo, useRef, useState } from "react";
import Storage from "utils/Storage";
import Texts from "utils/Texts";
import UiRoutes from "const/UiRoutes";
import Urls from "utils/Urls";
import classNames from "classnames";

const {
  ARCHIVE,
  AUDIT,
  CONTACTS,
  DOCUMENTS,
  TASKS
} = UiRoutes;

const Navbar = () => {
  const { params: { currentRoute } = {} } = useRouteMatch("/:businessId?/:currentRoute?");

  const { countryCode } = useSelector(getActiveOrganization);

  const projectName = useSelector(getProjectName);

  const { uiTexts, messages } = useSelector(getTextsData);

  const globalStatsData = useSelector(getGlobalStats);

  const documentsCurrentUploadCount = useSelector(getUploadingDocumentsCount);

  const archiveFilesCurrentUploadCount = useSelector(getUploadingArchiveFilesCount);

  const contactsData = useSelector(getContactsData);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const [firstContact] = [] || contactsData;

  const selectedBusinessId = useSelector(getSelectedBusinessId);

  const { extraData: { integrationService } = {} } = useSelector(getSelectedBusinessData);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const businessUser = useSelector(checkIsBusinessUser);

  const userBusinesses = useSelector(getUserBusinessesData);

  const activeOrganization = useSelector(getActiveOrganization);

  const tasksReadyToDisplay = useSelector(checkTasksReadyToDisplay);

  const singleBusiness = userBusinesses.length === 1;

  const czCountry = countryCode === Countries.CZ;

  const { whiteLabel: { logoUrl } = {} } = activeOrganization || {};

  const [extensionModalShown, setExtensionModalShown] = useState(false);

  const extensionButton = useRef();

  const { label: serviceName } = IntegrationServices.getByValue(integrationService) || {};

  const extensionAvailable = !!(
    rpaMode
    && !businessUser
    && !Platforms.getMobileType()
    && Platforms.checkIsChromiumBrowser()
  );

  const handleExtensionClick = useCallback(() => {
    setExtensionModalShown(true);
  }, []);

  const handleExtensionClose = useCallback(() => {
    setExtensionModalShown(false);
    extensionButton.current?.focus();
  }, []);

  const pagesList = useMemo(() => {
    return Object
      .values(Pages.getPageData(undefined, businessUser))
      .filter(({ value }) => {
        if (!Pages.checkPageAccess(value, businessUser, czCountry)) return false;
        switch (value) {
          case AUDIT:
            return quickBooksBusiness || xeroBusiness;
          case CONTACTS:
            return !firstContact || !firstContact.short;
          default:
            return true;
        }
      });
  }, [
    czCountry,
    businessUser,
    firstContact,
    quickBooksBusiness,
    xeroBusiness
  ]);

  return (
    <div className={Css.navbar}>
      <div className={Css.inner}>
        <div className={Css.logoWrap}>
          <div className={Css.logo} title={projectName}>
            <img src={logoUrl || coloredLogo} />
          </div>
        </div>
        {!businessUser && !singleBusiness && (
          <a
            href={UiRoutes.MAIN}
            className={Css.navItem}
            title={uiTexts.toAllClients}>
            <div className={Css.content}>
              <div className={Css.icon}>
                <Icons.ArrowLeft />
              </div>
              <span className={Css.text}>
                <span>{uiTexts.toAllClients}</span>
              </span>
            </div>
          </a>
        )}
        {pagesList.map((pageData) => {
          const {
            value: route,
            disabled,
            titleLangId,
            urlParameters,
            statsNotificationProp,
            statsNotificationUrlParameters,
            iconComponent: Icon
          } = pageData;

          const statsNotification = (route !== TASKS || tasksReadyToDisplay)
            && Objects.getPropertyByPath(globalStatsData, statsNotificationProp);

          const searchString = (
            statsNotification && statsNotificationUrlParameters
          ) ? Urls.objectToQueryString({ ...urlParameters, ...statsNotificationUrlParameters }) : "";

          const uploading = (route === DOCUMENTS && documentsCurrentUploadCount)
            || (route === ARCHIVE && archiveFilesCurrentUploadCount);

          const badgeValue = uploading || statsNotification;

          const pathData = {
            pathname: `/${selectedBusinessId}${route}${
              route === TASKS ? `/${Storage.storageValue(LocalStorageKeys.TASKS_VIEW_LAST_TAB) || "list"}` : ""
            }`,
            search: searchString ? `?${searchString}` : "",
            state: currentRoute === route.slice(1) ? { seed: Math.random() } : null
          };

          return (
            <NavLink
              key={route}
              disabled={disabled}
              className={classNames(Css.navItem, uploading && Css.uploading)}
              activeClassName={Css.active}
              to={pathData}>
              <div className={Css.content}>
                <div className={Css.icon}>
                  {uploading ? <Icons.CloudArrowUp data-warning /> : <Icon />}
                </div>
                <span className={Css.text}>
                  <span>{uiTexts[titleLangId]}</span>
                </span>
                {!!badgeValue && (
                  <Badge counter theme="attention" className={Css.notification}>
                    {badgeValue}
                  </Badge>
                )}
              </div>
            </NavLink>
          );
        })}
        {extensionAvailable && (
          <button
            ref={extensionButton}
            type="button"
            className={Css.extension}
            onClick={handleExtensionClick}>
            <span className={Css.wrap}>
              <span className={Css.extensionIcon}>
                <img src={extensionLogo} alt="" />
              </span>
              <span className={Css.title}>{uiTexts.browserExtension}</span>
              <span className={Css.description}>
                {Texts.replaceVars(messages.extensionDescription, { serviceName })}
              </span>
            </span>
          </button>
        )}
      </div>
      {extensionModalShown && (
        <BrowserExtensionModal integrationService={integrationService} onClose={handleExtensionClose} />
      )}
    </div>
  );
};

export default React.memo(Navbar);
